import type { GameStateOverlayLabels } from '@bn-games/ui-shell';
import type { Color } from '../game/types.js';
import { t } from '../i18n/I18n.js';

/**
 * Build localized labels for the DOM-modal game-state overlay — mirrors the
 * canvas-overlay label builder this file replaced. The rematch-preview
 * subtitle depends on the upcoming color (rematches swap sides), so the
 * caller resolves it fresh on each update.
 */
export function buildGameStateOverlayLabels(upcomingColor: Color | null): GameStateOverlayLabels {
  const rematchSubtitle =
    upcomingColor !== null
      ? t('preGame.rematchPreview', { side: t(`common.${upcomingColor}`) })
      : t('online.overlay.gameOverPrompt');

  return {
    opponentDisconnected: (name, countdown) => ({
      title: t('online.overlay.opponentDisconnected', { name }),
      body: t('online.overlay.waitingForReconnection', { countdown }),
    }),
    reconnectingSelf: (countdown) => ({
      title: t('online.overlay.reconnecting'),
      body: t('online.overlay.restoringGame', { countdown }),
    }),
    gameDisconnected: () => ({
      title: t('online.overlay.connectionLost'),
      body: t('online.overlay.gameEnded'),
    }),
    rematchOffered: (name) => ({
      title: t('online.overlay.rematchOffered'),
      body: t('online.overlay.waitingForOpponent', { name }),
    }),
    rematchRequested: (name) => ({
      title: t('online.overlay.rematchRequested', { name }),
    }),
    rematchDeclined: (name) => ({
      title: t('online.overlay.rematchDeclined', { name }),
    }),
    gameOver: (result) => ({
      title:
        result === 'win'
          ? t('online.overlay.gameOverWin')
          : result === 'loss'
            ? t('online.overlay.gameOverLoss')
            : t('online.overlay.gameOverDraw'),
      body: rematchSubtitle,
    }),
    buttons: {
      leave: t('online.overlay.buttons.leave'),
      rematch: t('online.overlay.buttons.rematch'),
      'return-to-menu': t('online.overlay.buttons.returnToMenu'),
      cancel: t('online.overlay.buttons.cancel'),
      accept: t('online.overlay.buttons.accept'),
      decline: t('online.overlay.buttons.decline'),
    },
  };
}
